import clsx from 'clsx'
import { chartSemanticProgressColor } from '@/config/chartTheme'
import type { ScorecardMetric, ScorecardPanelData } from '@/types/dashboards'
import { NO_VALUE } from './FigureValue'
import { useDrillDown } from './useDrillDown'
import ScoreDial from './ScoreDial'

function ScoreBar({ metric }: { metric: ScorecardMetric }) {
  const { open } = useDrillDown()
  const value = metric.value
  const clamped = value == null ? 0 : Math.max(0, Math.min(100, value))

  const label = (
    <span className="truncate text-[11px] text-stone-300" title={metric.label}>
      {metric.label}
    </span>
  )

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        {metric.drill ? (
          <button
            type="button"
            onClick={() => open(metric.drill)}
            title={`${metric.drill.title} — open records`}
            className="min-w-0 text-left underline decoration-amber-400/25 decoration-dotted underline-offset-[3px] transition-colors hover:text-amber-200 hover:decoration-amber-300/70 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
          >
            {label}
          </button>
        ) : (
          label
        )}
        <span
          className={clsx(
            'flex-shrink-0 font-mono text-[11px] tabular-nums',
            value == null ? 'text-stone-600' : 'text-stone-100',
          )}
        >
          {value == null
            ? NO_VALUE
            : metric.format === 'pct'
              ? `${value.toFixed(1)}%`
              : value.toFixed(0)}
        </span>
      </div>

      <div className="chart-viz-track relative h-1.5 w-full rounded-full">
        {value != null && (
          <div
            className="chart-viz-bar chart-viz-bar--rounded h-full"
            style={{ width: `${clamped}%`, background: chartSemanticProgressColor(clamped) }}
          />
        )}
        {metric.target != null && (
          <div
            className="absolute top-[-2px] h-[10px] w-px bg-stone-400/70"
            style={{ left: `${Math.max(0, Math.min(100, metric.target))}%` }}
            title={`Target ${metric.target.toFixed(0)}`}
          />
        )}
      </div>

      {metric.detail && <p className="text-[10px] text-stone-500">{metric.detail}</p>}
    </div>
  )
}

interface ScorecardPanelProps {
  data: ScorecardPanelData
  /** Show the large overall ring above the bars. */
  showDial?: boolean
  dialSize?: number
  className?: string
}

/** Labeled 0–100 bars with target markers, above an optional overall ring. */
export default function ScorecardPanel({
  data,
  showDial = true,
  dialSize = 132,
  className,
}: ScorecardPanelProps) {
  return (
    <div className={clsx('flex min-h-0 flex-col gap-3', className)}>
      {showDial && <ScoreDial figure={data.overall} size={dialSize} />}
      <div className="space-y-2.5">
        {data.metrics.map((metric) => (
          <ScoreBar key={metric.key} metric={metric} />
        ))}
      </div>
      {data.footnote && (
        <p className="text-[10px] leading-relaxed text-stone-500">{data.footnote}</p>
      )}
    </div>
  )
}
